import { siteInfo } from '@/mocks/mangaData';

interface SEOSchemaProps {
  type: 'Organization' | 'WebSite' | 'WebPage' | 'FAQPage';
  pageTitle?: string;
  pageDescription?: string;
  pageUrl?: string;
  faqData?: { question: string; answer: string }[];
  breadcrumbData?: { name: string; url: string }[];
  articleData?: {
    title: string;
    description: string;
    imageUrl: string;
    datePublished: string;
    authorName: string;
  };
}

export default function SEOSchema({
  type,
  pageTitle,
  pageDescription,
  pageUrl,
  faqData,
  articleData,
}: SEOSchemaProps) {
  const baseUrl = 'https://blmanga-select.com';

  const getSchema = () => {
    if (type === 'Organization') {
      return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: siteInfo.name,
        url: baseUrl,
        description: siteInfo.description,
        contactPoint: {
          '@type': 'ContactPoint',
          email: siteInfo.email,
          contactType: 'customer service',
        },
      };
    }
    if (type === 'WebSite') {
      return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: siteInfo.name,
        url: baseUrl,
        description: siteInfo.description,
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${baseUrl}/search?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      };
    }
    if (type === 'WebPage') {
      return {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: pageTitle,
        description: pageDescription,
        url: pageUrl ? `${baseUrl}${pageUrl}` : baseUrl,
        isPartOf: {
          '@type': 'WebSite',
          name: siteInfo.name,
          url: baseUrl,
        },
      };
    }
    if (type === 'FAQPage') {
      return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: (faqData || []).map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      };
    }
    return null;
  };

  const schema = getSchema();
  if (!schema) return null;

  return <script type="application/ld+json">{JSON.stringify(schema)}</script>;
}

export function ArticleSchema({
  article,
}: {
  article: {
    title: string;
    description: string;
    imageUrl: string;
    datePublished: string;
    authorName: string;
  };
}) {
  const baseUrl = 'https://blmanga-select.com';
  return (
    <script type="application/ld+json">
      {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.description,
        image: article.imageUrl,
        datePublished: article.datePublished,
        author: {
          '@type': 'Person',
          name: article.authorName,
        },
        publisher: {
          '@type': 'Organization',
          name: siteInfo.name,
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': baseUrl,
        },
      })}
    </script>
  );
}